import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Spinner,
  Card,
  Modal,
  Row,
  Col,
  Pagination,
  Badge,
  InputGroup,
  Form,
} from "react-bootstrap";
import {
  Eye,
  Calendar,
  Search,
  ArrowClockwise,
  FileEarmarkPdf,
  Building,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const ContractManagement = () => {
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedContract, setSelectedContract] = useState(null);
  const [showView, setShowView] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH CONTRACTS ================= */
  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/formContract/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: currentPage, limit },
      });

      if (res.data.success) {
        setContracts(res.data.data || []);
        setFilteredContracts(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [currentPage]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContracts(contracts);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const filtered = contracts.filter(
      (c) =>
        c.name?.toLowerCase().includes(lower) ||
        c.type?.toLowerCase().includes(lower) ||
        c.project?.name?.toLowerCase().includes(lower)
    );

    setFilteredContracts(filtered);
  }, [searchTerm, contracts]);

  /* ================= PAGINATION ================= */
  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3 justify-content-between">
            {/* TITLE */}
            <Col md={6}>
              <h4 className="fw-bold mb-0">Contract Management</h4>
              <small className="text-muted">
                View uploaded contracts and documents
              </small>
            </Col>

            {/* SEARCH + REFRESH */}
            <Col md={6}>
              <div className="d-flex">
                <InputGroup>
                  <InputGroup.Text>
                    <Search />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by contract, project or type"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>

                <Button
                  variant="outline-primary"
                  className="ms-2"
                  title="Refresh"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                    fetchContracts();
                  }}
                >
                  <ArrowClockwise />
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE ===== */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Contract Name</th>
                      <th>Project</th>
                      <th>Type</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContracts.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No contracts found
                        </td>
                      </tr>
                    ) : (
                      filteredContracts.map((c) => (
                        <tr key={c._id}>
                          <td className="fw-semibold">
                            <FileEarmarkPdf className="me-2 text-danger" />
                            {c.name}
                          </td>
                          <td>
                            <Building className="me-1" />
                            {c.project?.name || "-"}
                          </td>
                          <td>
                            <Badge bg="info">
                              {c.type === "Miscellaneous"
                                ? c.otherTypeName
                                : c.type}
                            </Badge>
                          </td>
                          <td>
                            <Calendar className="me-1" />
                            {new Date(c.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => {
                                setSelectedContract(c);
                                setShowView(true);
                              }}
                            >
                              <Eye />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="p-3 d-flex justify-content-end">
                  <Pagination className="mb-0">
                    {renderPagination()}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ===== VIEW MODAL ===== */}
      <Modal show={showView} onHide={() => setShowView(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Contract Details – {selectedContract?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContract && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p>
                    <strong>Type:</strong>{" "}
                    {selectedContract.type === "Miscellaneous"
                      ? selectedContract.otherTypeName
                      : selectedContract.type}
                  </p>
                  <p>
                    <strong>File:</strong> {selectedContract.fileName}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Project:</strong>{" "}
                    {selectedContract.project?.name}
                  </p>
                  <p>
                    <strong>Location:</strong>{" "}
                    {selectedContract.project?.projectLocation}
                  </p>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={() =>
                    window.open(selectedContract.fileUrl, "_blank")
                  }
                >
                  <FileEarmarkPdf className="me-2" />
                  View / Download File
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ContractManagement;
